import React, { useEffect, useImperativeHandle, useRef } from 'react'
import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'
import styled from 'styled-components'

type Props<T> = T extends React.Component<infer P> | React.FC<infer P> ? P : never

export type ScrollViewProps = React.PropsWithChildren<
  Omit<Props<SimpleBar>, 'onScroll'> & {
    disableWindowScroll?: boolean
    onScroll?: () => void
  }
>
export type ScrollViewRef = {
  scrollTo: (position: { x?: number; y?: number }, animate?: boolean) => void
  getChildren: () => HTMLElement[]
  getChildById: (id: string) => HTMLElement | undefined
  getScrollElement: () => HTMLElement | undefined
}
const ScrollView = styled(
  React.forwardRef<ScrollViewRef, ScrollViewProps>(function ScrollView(
    { disableWindowScroll, onScroll, ...props },
    ref
  ) {
    const simplebar = useRef<SimpleBar>(null)

    useEffect(() => {
      if (disableWindowScroll) {
        $('html').animate({ scrollTop: 0 }, 300)
        document.body.style.overflow = 'hidden'
      }
      return () => {
        document.body.style.overflow = 'auto'
      }
    }, [disableWindowScroll])

    useImperativeHandle(
      ref,
      () => {
        return {
          getChildren: () => Array.from(simplebar.current?.contentEl.children ?? []) as HTMLElement[],
          getChildById: (id: string) =>
            Array.from(simplebar.current?.contentEl.children ?? []).find((x) => x.id === id) as HTMLElement | undefined,
          scrollTo: ({ x, y }, animate = true) => {
            if (!simplebar.current) {
              return
            }
            const el = $(simplebar.current.contentWrapperEl)
            const scrollLeft = x ?? el.scrollLeft() ?? 0
            const scrollTop = y ?? el.scrollTop() ?? 0
            if (animate) {
              el.stop().animate({ scrollTop, scrollLeft }, animate === true ? 600 : animate)
            } else {
              el.stop().scrollTop(scrollTop).scrollLeft(scrollLeft)
            }
          },
          getScrollElement: () => simplebar.current?.contentWrapperEl
        }
      },
      []
    )

    useEffect(() => {
      const scrollEl = simplebar.current?.contentWrapperEl
      if (!scrollEl || !onScroll) {
        return
      }
      scrollEl.addEventListener('scroll', onScroll, { passive: true })
      return () => {
        scrollEl.removeEventListener('scroll', onScroll)
      }
    }, [onScroll])

    return <SimpleBar {...props} ref={simplebar} />
  })
)`
  height: 100%;
`

export default ScrollView
